'use client';
import { useState } from 'react';
import posthog from 'posthog-js';
import { submitFeedback } from '@/services/userLoginApi';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Iconify } from '@/components/common/iconify';

const CATEGORIES = [
  { value: 'bug', label: 'Bug report' },
  { value: 'feature', label: 'Feature request' },
  { value: 'general', label: 'General feedback' },
];

export default function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('general');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setSubmitted(false);
    setMessage('');
    setCategory('general');
  };

  const handleClose = () => setOpen(false);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    const page = typeof window !== 'undefined' ? window.location.pathname : '';
    const posthog_replay_url = (posthog as any).get_session_replay_url?.({ withTimestamp: true }) ?? undefined;
    try {
      await submitFeedback({ category, message: message.trim(), page, posthog_replay_url });
      posthog.capture('user_feedback', { category, message: message.trim(), page });
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Docked to the right edge, vertically centered — always in the same
          spot (unlike the old draggable pill) and out of the way of both the
          bottom-right chatbot launcher and page content. */}
      <Box sx={{ position: 'fixed', top: '50%', right: 0, transform: 'translateY(-50%)', zIndex: 1150 }}>
        <Button
          onClick={handleOpen}
          variant="contained"
          sx={{
            minWidth: 0,
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
            px: 1,
            py: 1.5,
            borderRadius: '10px 0 0 10px',
            boxShadow: (theme) => theme.customShadows.z12,
            textTransform: 'none',
            fontSize: '0.9375rem',
            fontWeight: 600,
          }}
        >
          <Box sx={{ width: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Iconify icon="mdi:message-text-outline" width={20} />
          </Box>
          <Box
            component="span"
            sx={{
              width: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
            }}
          >
            Feedback
          </Box>
        </Button>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            boxShadow: (theme) => theme.customShadows.dialog,
            bgcolor: 'background.paper',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: (theme) => (theme.palette.mode === 'light' ? 'brand.darkTeal' : 'text.primary'),
            }}
          >
            Send feedback
          </Typography>
          <IconButton size="small" aria-label="Close" onClick={handleClose}>
            <Iconify icon="mdi:close" width={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 0 }}>
          {submitted ? (
            <Box
              sx={{
                py: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Iconify icon="mdi:check-circle-outline" width={48} color="success.main" />
              <Typography variant="body1" fontWeight={600} textAlign="center">
                Thanks for your feedback!
              </Typography>
              <Typography variant="body2" color="text.primary" textAlign="center">
                We read every submission and use it to improve the platform.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClose}
                sx={{
                  mt: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: (theme) => (theme.palette.mode === 'light' ? '8px' : '12px'),
                }}
              >
                Close
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.primary" sx={{ mb: 1, display: 'block' }}>
                  Category
                </Typography>
                <Select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  size="small"
                  fullWidth
                  sx={{
                    borderRadius: '12px',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'divider',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  }}
                >
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>
                      {c.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box>
                <Typography variant="caption" color="text.primary" sx={{ mb: 1, display: 'block' }}>
                  Message
                </Typography>
                <TextField
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  multiline
                  rows={4}
                  placeholder="Describe your experience, report a bug, or suggest an improvement…"
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 1000 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '& fieldset': {
                        borderColor: 'divider',
                      },
                      '&:hover fieldset': {
                        borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '1px',
                      },
                    },
                  }}
                />
                <Typography variant="caption" color="text.disabled" sx={{ float: 'right', mt: 1 }}>
                  {message.length}/1000
                </Typography>
              </Box>

              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!message.trim() || submitting}
                fullWidth
                sx={{
                  mt: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: (theme) => (theme.palette.mode === 'light' ? '8px' : '12px'),
                }}
              >
                {submitting ? 'Sending…' : 'Submit'}
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
